import { useRef, useCallback, useEffect, useState } from 'react';
import { Animated, Platform } from 'react-native';
import { durations } from '../theme/motion';

const USE_NATIVE_DRIVER = Platform.OS !== 'web';

export const SPRING_CONFIG_SNAPPY = {
  friction: 8,
  tension: 100,
  useNativeDriver: USE_NATIVE_DRIVER,
};

// Quick response for interactive elements
const SPRING_CONFIG_QUICK = {
  friction: 6,
  tension: 150,
  useNativeDriver: USE_NATIVE_DRIVER,
};

/**
 * Hook for clear button fade animation in search bars
 */
export function useClearButtonAnimation(hasValue: boolean) {
  const opacity = useRef(new Animated.Value(hasValue ? 1 : 0)).current;
  const scale = useRef(new Animated.Value(hasValue ? 1 : 0.8)).current;

  const show = useCallback(() => {
    Animated.parallel([
      Animated.timing(opacity, {
        toValue: 1,
        duration: durations.quick,
        useNativeDriver: USE_NATIVE_DRIVER,
      }),
      Animated.spring(scale, {
        toValue: 1,
        ...SPRING_CONFIG_QUICK,
      }),
    ]).start();
  }, [opacity, scale]);

  const hide = useCallback(() => {
    Animated.parallel([
      Animated.timing(opacity, {
        toValue: 0,
        duration: durations.quick,
        useNativeDriver: USE_NATIVE_DRIVER,
      }),
      Animated.timing(scale, {
        toValue: 0.8,
        duration: durations.quick,
        useNativeDriver: USE_NATIVE_DRIVER,
      }),
    ]).start();
  }, [opacity, scale]);

  return {
    opacity,
    scale,
    show,
    hide,
  };
}

/**
 * Fade an overlay in and out, and keep it out of hit-testing until it is
 * actually on screen.
 *
 * `shown` must be the full condition for the overlay being drawn, gates
 * included — not just a `visible` flag. That matters because of how RN tears
 * animated nodes down: when the `Animated.View` unmounts, `AnimatedProps` and
 * `AnimatedStyle` detach from the value, the value loses its last child, and
 * `AnimatedValue.__detach()` calls `stopAnimation()`, which leaves the value
 * frozen wherever it stood. A caller whose fade effect keys off `visible`
 * alone never restarts it, so a gate that closes inside the fade brings the
 * overlay back invisible and, with default pointerEvents, still clickable.
 * That is what took the plan-map corner handles on Sept 18 2026: the welcome
 * block sat over the middle of the map at opacity 0 and ate the mousedown.
 *
 * So two rules, and callers get both for free:
 *   - the fade restarts whenever `shown` turns true, frozen value or not;
 *   - `pointerEvents` is 'auto' only after a fade-in that actually finished.
 *     An interrupted one reports `finished: false`, and a starved rAF loop
 *     (occluded tab) never reports at all, so neither can leave an invisible
 *     overlay taking clicks.
 *
 * Put `opacity` in the style of the `Animated.View` that carries the fade and
 * `pointerEvents` on it as a prop. The prop, not a style entry: RN-web
 * compiles the prop into real pointer-events rules (including the child rules
 * 'box-none' needs), while its inline-style path drops pointerEvents.
 */
export function useOverlayFade(
  shown: boolean,
  opts?: {
    inDuration?: number;
    outDuration?: number;
    /** What `pointerEvents` becomes once the fade-in has finished. Use
     *  'box-none' when the animated view is a full-width positioning wrapper
     *  and only its children should take clicks — 'auto' there would put an
     *  invisible clickable strip across whatever is underneath. */
    whenShown?: 'auto' | 'box-none';
  }
) {
  const inDuration = opts?.inDuration ?? 220;
  const outDuration = opts?.outDuration ?? 180;
  const whenShown = opts?.whenShown ?? 'auto';
  const opacity = useRef(new Animated.Value(0)).current;
  const [interactive, setInteractive] = useState(false);
  const fadingOutRef = useRef(false);

  useEffect(() => {
    // Off screen: drop the flag so a re-show can't inherit "clickable" from
    // the last time it was up.
    if (!shown) {
      setInteractive(false);
      return;
    }
    // A dismissal already owns the value — don't animate against it.
    if (fadingOutRef.current) return;
    setInteractive(false);
    Animated.timing(opacity, {
      toValue: 1,
      duration: inDuration,
      // Read per call, not from the module-level constant: the constant is
      // frozen at import time, which a test can't get ahead of.
      useNativeDriver: Platform.OS !== 'web',
    }).start(({ finished }) => {
      if (finished) setInteractive(true);
    });
  }, [shown, opacity, inDuration]);

  /** Fade out, then run `done`. Stops taking clicks immediately rather than
   *  when the animation lands. `done` runs even if the fade was cut short by
   *  a gate closing: the overlay was dismissed either way, and the caller
   *  needs to hear about it to clear its own state. */
  const fadeOut = useCallback(
    (done?: () => void) => {
      fadingOutRef.current = true;
      setInteractive(false);
      Animated.timing(opacity, {
        toValue: 0,
        duration: outDuration,
        useNativeDriver: Platform.OS !== 'web',
      }).start(() => {
        fadingOutRef.current = false;
        // Land on a clean 0 — an interrupted fade stops partway, and the next
        // show would otherwise start from that leftover value.
        opacity.setValue(0);
        done?.();
      });
    },
    [opacity, outDuration]
  );

  return {
    opacity,
    pointerEvents: interactive ? whenShown : ('none' as const),
    fadeOut,
  };
}
